(() => {
  const sectionSelector = "[data-orela-product-section]";

  const modulo = (value, length) => (value + length) % length;

  const initializeGallery = (root, controller, cleanups) => {
    const gallery = root.querySelector("[data-orela-gallery]");
    const track = gallery?.querySelector("[data-orela-media-track]");
    const slides = track ? [...track.querySelectorAll("[data-orela-media-slide]")] : [];
    const thumbnails = gallery ? [...gallery.querySelectorAll("[data-orela-media-thumb]")] : [];
    const previousButton = gallery?.querySelector("[data-orela-media-prev]");
    const nextButton = gallery?.querySelector("[data-orela-media-next]");
    const lightbox = root.querySelector("[data-orela-lightbox]");
    const lightboxDialog = lightbox?.querySelector(".orela-lightbox-dialog");
    const lightboxImage = lightbox?.querySelector("[data-orela-lightbox-image]");
    const lightboxCounter = lightbox?.querySelector("[data-orela-lightbox-counter]");
    const lightboxClose = lightbox?.querySelector("[data-orela-lightbox-close]");
    const lightboxPrevious = lightbox?.querySelector("[data-orela-lightbox-prev]");
    const lightboxNext = lightbox?.querySelector("[data-orela-lightbox-next]");

    if (!track || !slides.length) return;

    let currentIndex = 0;
    let lightboxIndex = 0;
    let scrollFrame = null;
    let pendingScrollIndex = null;
    let lastFocusedElement = null;
    const requestedImages = new Set();

    const closestSlideIndex = () =>
      slides.reduce((closest, slide, index) => {
        const currentDistance = Math.abs(slide.offsetLeft - track.scrollLeft);
        const closestDistance = Math.abs(slides[closest].offsetLeft - track.scrollLeft);
        return currentDistance < closestDistance ? index : closest;
      }, currentIndex);

    const primeImage = (index, priority = "auto") => {
      const slide = slides[modulo(index, slides.length)];
      const image = slide?.querySelector("img");
      const imageSource = image?.dataset.src || image?.getAttribute("src");
      if (!image || !imageSource) return;

      let absoluteSource = imageSource;
      try {
        absoluteSource = new URL(imageSource, window.location.href).href;
      } catch (_error) {
        // Shopify asset URLs should always be valid. Keep the original URL as a safe fallback.
      }

      if (requestedImages.has(absoluteSource)) return;
      requestedImages.add(absoluteSource);
      image.loading = "eager";
      image.decoding = "async";
      image.fetchPriority = priority;

      if (image.dataset.src) {
        image.src = image.dataset.src;
        image.removeAttribute("data-src");
      }
    };

    const scheduleImage = (index) => {
      const load = () => primeImage(index, "low");
      if ("requestIdleCallback" in window) {
        const requestId = window.requestIdleCallback(load, { timeout: 1800 });
        cleanups.push(() => window.cancelIdleCallback?.(requestId));
      } else {
        const timeoutId = window.setTimeout(load, 700);
        cleanups.push(() => window.clearTimeout(timeoutId));
      }
    };

    const setActiveSlide = (index, shouldScroll = false) => {
      currentIndex = modulo(index, slides.length);

      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-active", slideIndex === currentIndex);
      });

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === currentIndex;
        thumbnail.classList.toggle("is-active", isActive);
        thumbnail.setAttribute("aria-selected", String(isActive));
        thumbnail.tabIndex = isActive ? 0 : -1;
      });

      if (!shouldScroll) return;

      primeImage(currentIndex, "high");
      scheduleImage(currentIndex + 1);
      const targetLeft = slides[currentIndex].offsetLeft;
      pendingScrollIndex = currentIndex;
      track.scrollTo({
        left: targetLeft,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
      if (Math.abs(track.scrollLeft - targetLeft) < 1) pendingScrollIndex = null;

      const activeThumbnail = thumbnails[currentIndex];
      const thumbnailTrack = activeThumbnail?.parentElement;
      if (activeThumbnail && thumbnailTrack) {
        thumbnailTrack.scrollTo({
          left: Math.max(0, activeThumbnail.offsetLeft - thumbnailTrack.clientWidth / 2),
          behavior: "smooth",
        });
      }
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener("click", () => setActiveSlide(index, true), { signal: controller.signal });
      thumbnail.addEventListener(
        "keydown",
        (event) => {
          let nextIndex = null;
          if (event.key === "ArrowLeft") nextIndex = currentIndex - 1;
          if (event.key === "ArrowRight") nextIndex = currentIndex + 1;
          if (event.key === "Home") nextIndex = 0;
          if (event.key === "End") nextIndex = thumbnails.length - 1;
          if (nextIndex === null) return;

          event.preventDefault();
          nextIndex = modulo(nextIndex, thumbnails.length);
          setActiveSlide(nextIndex, true);
          thumbnails[nextIndex]?.focus();
        },
        { signal: controller.signal },
      );
    });

    previousButton?.addEventListener("click", () => setActiveSlide(currentIndex - 1, true), {
      signal: controller.signal,
    });
    nextButton?.addEventListener("click", () => setActiveSlide(currentIndex + 1, true), {
      signal: controller.signal,
    });

    track.addEventListener(
      "pointerdown",
      () => {
        pendingScrollIndex = null;
      },
      { passive: true, signal: controller.signal },
    );

    track.addEventListener(
      "scroll",
      () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          if (pendingScrollIndex !== null) {
            const targetLeft = slides[pendingScrollIndex].offsetLeft;
            if (Math.abs(track.scrollLeft - targetLeft) < 1) pendingScrollIndex = null;
            return;
          }

          const closestIndex = closestSlideIndex();
          if (closestIndex !== currentIndex) setActiveSlide(closestIndex);
        });
      },
      { passive: true, signal: controller.signal },
    );
    track.addEventListener(
      "scrollend",
      () => {
        const wasProgrammatic = pendingScrollIndex !== null;
        pendingScrollIndex = null;
        if (!wasProgrammatic) {
          const closestIndex = closestSlideIndex();
          if (closestIndex !== currentIndex) setActiveSlide(closestIndex);
        }
      },
      { passive: true, signal: controller.signal },
    );
    cleanups.push(() => window.cancelAnimationFrame(scrollFrame));

    const showLightboxImage = (index) => {
      if (!lightboxImage || !lightboxCounter) return;
      lightboxIndex = modulo(index, slides.length);
      primeImage(lightboxIndex, "high");
      const sourceImage = slides[lightboxIndex].querySelector("img");
      if (!sourceImage) return;
      lightboxImage.src =
        sourceImage.dataset.fullSrc ||
        slides[lightboxIndex].dataset.fullSrc ||
        sourceImage.currentSrc ||
        sourceImage.getAttribute("src") ||
        "";
      lightboxImage.alt = sourceImage.alt;
      lightboxCounter.textContent = `${lightboxIndex + 1} / ${slides.length}`;
    };

    const openLightbox = (index) => {
      if (!lightbox || !lightboxImage) return;
      lastFocusedElement = document.activeElement;
      showLightboxImage(index);
      lightbox.hidden = false;
      document.body.classList.add("orela-lightbox-open");
      lightboxClose?.focus();
    };

    const closeLightbox = () => {
      if (!lightbox || lightbox.hidden) return;
      lightbox.hidden = true;
      lightboxImage?.removeAttribute("src");
      document.body.classList.remove("orela-lightbox-open");
      lastFocusedElement?.focus();
    };

    slides.forEach((slide, index) => {
      const image = slide.querySelector("img");
      slide.setAttribute("role", "button");
      slide.tabIndex = 0;
      slide.setAttribute("aria-label", `Enlarge ${image?.alt || `product image ${index + 1}`}`);
      slide.addEventListener("click", () => openLightbox(index), { signal: controller.signal });
      slide.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          openLightbox(index);
        },
        { signal: controller.signal },
      );
    });

    lightboxClose?.addEventListener("click", closeLightbox, { signal: controller.signal });
    lightboxPrevious?.addEventListener("click", () => showLightboxImage(lightboxIndex - 1), {
      signal: controller.signal,
    });
    lightboxNext?.addEventListener("click", () => showLightboxImage(lightboxIndex + 1), {
      signal: controller.signal,
    });
    lightbox?.addEventListener(
      "click",
      (event) => {
        if (event.target === lightbox || event.target === lightboxDialog) closeLightbox();
      },
      { signal: controller.signal },
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (!lightbox || lightbox.hidden) return;

        if (event.key === "Escape") {
          closeLightbox();
        } else if (event.key === "ArrowLeft") {
          showLightboxImage(lightboxIndex - 1);
        } else if (event.key === "ArrowRight") {
          showLightboxImage(lightboxIndex + 1);
        } else if (event.key === "Tab") {
          const controls = [lightboxClose, lightboxPrevious, lightboxNext].filter(Boolean);
          const firstControl = controls[0];
          const lastControl = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === firstControl) {
            event.preventDefault();
            lastControl?.focus();
          } else if (!event.shiftKey && document.activeElement === lastControl) {
            event.preventDefault();
            firstControl?.focus();
          }
        }
      },
      { signal: controller.signal },
    );

    requestedImages.add(slides[0].querySelector("img")?.src || "");
    setActiveSlide(0);
    scheduleImage(1);

    if ("IntersectionObserver" in window) {
      const imageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = slides.indexOf(entry.target);
            if (index >= 0) primeImage(index, "low");
            imageObserver.unobserve(entry.target);
          });
        },
        { rootMargin: "600px 0px" },
      );
      slides.slice(1).forEach((slide) => imageObserver.observe(slide));
      cleanups.push(() => imageObserver.disconnect());
    }

    cleanups.push(() => {
      if (lightbox && !lightbox.hidden) document.body.classList.remove("orela-lightbox-open");
    });
  };

  const initializePurchaseOptions = (root, controller, cleanups) => {
    const purchaseOptions = root.querySelector(".orela-purchase-options");
    const productDataElement = purchaseOptions?.querySelector("[data-orela-product-data]");
    if (!purchaseOptions || !productDataElement) return;

    let productData;
    try {
      productData = JSON.parse(productDataElement.textContent);
    } catch (error) {
      console.error("Unable to read Orela product data.", error);
      return;
    }

    const variantOptions = [...purchaseOptions.querySelectorAll("[data-orela-variant-option]")];
    const subscriptionToggle = purchaseOptions.querySelector("[data-orela-subscription-toggle]");
    const subscriptionCard = subscriptionToggle?.closest("label");
    const sellingPlanOptions = [...purchaseOptions.querySelectorAll("[data-orela-selling-plan-option]")];
    const variantIdInput = purchaseOptions.querySelector("[data-orela-variant-id]");
    const sellingPlanInput = purchaseOptions.querySelector("[data-orela-selling-plan-input]");
    const sellingPlansContainer = purchaseOptions.querySelector("[data-orela-selling-plans]");
    const subscriptionRequired = purchaseOptions.querySelector("[data-orela-subscription-required]");
    const submitButton = purchaseOptions.querySelector("[data-orela-submit]");
    const submitText = purchaseOptions.querySelector("[data-orela-submit-text]");
    const soldOutMessage = submitButton?.querySelector(".sold-out-message");
    const primaryCta = purchaseOptions.querySelector("[data-orela-primary-cta]");
    const stickyCta = purchaseOptions.querySelector("[data-orela-sticky-cta]");
    const stickyText = purchaseOptions.querySelector("[data-orela-sticky-text]");
    const ctaContents = [...purchaseOptions.querySelectorAll("[data-orela-cta-content]")];

    const variants = Array.isArray(productData.variants) ? productData.variants : [];
    const twoStep = purchaseOptions.classList.contains("orela-purchase-options--two-step");
    const oneTimeOption = purchaseOptions.querySelector("[data-orela-one-time]");
    const oneTimeCard = oneTimeOption?.closest("label");
    const purchaseState = purchaseOptions.querySelector("[data-orela-purchase-state]");
    const labels = productData.labels || {};
    const translated = (key, values = {}) => Object.entries(values).reduce(
      (text, [name, value]) => text.replaceAll(`__${name}__`, String(value)),
      String(labels[key] || ""),
    );
    if (twoStep && (!productData.twoStep || !variants.length || variants.some((variant) =>
      !variant.id || !Number.isFinite(variant.price) || variant.price < 0 ||
      !Number.isInteger(variant.bottleCount) || variant.bottleCount < 1 || !variant.unitPriceFormatted ||
      !Array.isArray(variant.sellingPlans) || variant.sellingPlans.some((plan) =>
        !plan.id || !Number.isFinite(plan.price) || plan.price < 0 || !plan.unitPriceFormatted
      )
    ))) return;
    const addToCartLabel = String(productData.addToCartLabel || "Add to cart");
    const showCtaSavings = Boolean(productData.showCtaSavings);
    let subscriptionPreference = Boolean(subscriptionToggle?.checked);
    let currentPurchasable = false;
    const initialVariantId = variantOptions.find((option) => option.checked)?.value;

    const selectedVariant = () => {
      const checkedOption = variantOptions.find((option) => option.checked);
      return variants.find((variant) => String(variant.id) === checkedOption?.value) || variants[0];
    };

    const readPurchaseUrl = () => {
      if (!twoStep) return;
      const params = new URL(window.location.href).searchParams;
      const variantId = params.get("variant");
      const matchingVariant = variantOptions.find((option) => option.value === variantId) ||
        variantOptions.find((option) => option.value === initialVariantId);
      if (matchingVariant) matchingVariant.checked = true;
      const allocations = selectedVariant()?.sellingPlans || [];
      subscriptionPreference = Boolean(allocations.length);
      const requestedPlan = allocations.find((plan) => String(plan.id) === params.get("selling_plan"));
      if (requestedPlan) {
        const planOption = sellingPlanOptions.find((option) => option.value === String(requestedPlan.id));
        if (planOption) planOption.checked = true;
        subscriptionPreference = true;
      } else if (params.get("purchase_type") === "one-time") {
        subscriptionPreference = false;
      }
    };
    readPurchaseUrl();

    const updateTwoStepPrices = (variant, allocation) => {
      if (!twoStep) return;
      const subscriptionPrice = purchaseOptions.querySelector("[data-orela-subscription-unit-price]");
      const subscriptionCompare = purchaseOptions.querySelector("[data-orela-subscription-unit-compare]");
      const oneTimePrice = purchaseOptions.querySelector("[data-orela-one-time-unit-price]");
      const deliveryLabel = purchaseOptions.querySelector("[data-orela-delivery-label]");
      const shownAllocation = allocation || variant.sellingPlans[0];
      const cadence = shownAllocation?.name?.match(/every\s+(?:(\d+)\s+)?months?\b/i);
      const months = cadence ? Number(cadence[1] || 1) : null;
      const showCompare = shownAllocation && variant.compareAtPrice > shownAllocation.price;
      subscriptionPrice.textContent = shownAllocation?.unitPriceFormatted || "—";
      subscriptionCompare.textContent = showCompare ? variant.unitCompareAtPriceFormatted : "";
      subscriptionCompare.hidden = !showCompare;
      oneTimePrice.textContent = variant.unitPriceFormatted;
      deliveryLabel.textContent = months ? translated("delivery", { months }) : (shownAllocation?.name || labels.subscriptionUnavailable);
      const deliveryBenefit = purchaseOptions.querySelector("[data-orela-delivery-benefit]");
      if (deliveryBenefit) {
        const matchingSupply = variant.bottleCount === months;
        const deliveryCopy = matchingSupply && months === 2 ? deliveryBenefit.dataset.copyTwo :
          matchingSupply && months === 3 ? deliveryBenefit.dataset.copyThree : "";
        deliveryBenefit.hidden = !deliveryCopy;
        deliveryBenefit.querySelector("[data-orela-delivery-benefit-text]").textContent = deliveryCopy || "";
      }
    };

    const updateBundlePrices = (subscriptionSelected = false, preferredSellingPlanId = null) => {
      variantOptions.forEach((option) => {
        const variant = variants.find((candidate) => String(candidate.id) === option.value);
        const label = option.closest("label");
        const price = label?.querySelector("[data-orela-bundle-price]");
        const compareAtPrice = label?.querySelector("[data-orela-bundle-compare-price]");
        if (!variant) return;

        const allocation = subscriptionSelected
          ? variant.sellingPlans?.find((plan) => String(plan.id) === String(preferredSellingPlanId)) ||
            variant.sellingPlans?.[0]
          : null;
        const displayedPrice = allocation || variant;
        const displayedCompareAtPrice =
          Number(variant.compareAtPrice) > Number(displayedPrice.price)
            ? variant.compareAtPriceFormatted
            : "";

        if (price) price.textContent = displayedPrice.priceFormatted;
        if (twoStep) {
          const savingElement = label?.querySelector("[data-orela-bundle-saving]");
          const saving = variant.compareAtPrice > displayedPrice.price
            ? Math.round((variant.compareAtPrice - displayedPrice.price) / variant.compareAtPrice * 1000) / 10
            : 0;
          if (savingElement) {
            savingElement.textContent = translated("save", { percent: saving });
            savingElement.hidden = saving <= 0;
          }
        }
        if (compareAtPrice) {
          compareAtPrice.textContent = displayedCompareAtPrice;
          compareAtPrice.hidden = !displayedCompareAtPrice;
        }
      });
    };

    const updateSelectedStyles = () => {
      variantOptions.forEach((option) => option.closest("label")?.classList.toggle("is-selected", option.checked));
      subscriptionCard?.classList.toggle("is-selected", Boolean(subscriptionToggle?.checked));
      if (twoStep) oneTimeCard?.classList.toggle("is-selected", Boolean(oneTimeOption?.checked));
      sellingPlanOptions.forEach((option) =>
        option.closest("label")?.classList.toggle("is-selected", option.checked),
      );
    };

    const clearProductFormError = () => {
      if (submitButton?.classList.contains("loading")) return;
      purchaseOptions.error = false;
      purchaseOptions.handleErrorMessage?.();
      submitText?.classList.remove("hidden");
      soldOutMessage?.classList.add("hidden");
    };

    const updateCtaContents = (label, compareAtPrice = "", salePrice = "") => {
      const showPricePair = Boolean(compareAtPrice && salePrice);

      ctaContents.forEach((content) => {
        const labelElement = content.querySelector("[data-orela-cta-label]");
        const compareAtElement = content.querySelector("[data-orela-cta-compare-price]");
        const salePriceElement = content.querySelector("[data-orela-cta-sale-price]");

        if (labelElement) labelElement.textContent = label;
        if (compareAtElement) {
          compareAtElement.textContent = compareAtPrice;
          compareAtElement.hidden = !showPricePair;
        }
        if (salePriceElement) {
          salePriceElement.textContent = salePrice;
          salePriceElement.hidden = twoStep ? !salePrice : !showPricePair;
        }
      });

      if (!ctaContents.length) {
        if (submitText) submitText.textContent = label;
        if (stickyText) stickyText.textContent = label;
      }
    };

    const synchronizeStickySubmitState = () => {
      if (!stickyCta || !submitButton) return;
      const isBusy = submitButton.classList.contains("loading");
      const isDawnDisabled = submitButton.getAttribute("aria-disabled") === "true";
      const stickySpinner = stickyCta.querySelector(".loading__spinner");
      stickyCta.disabled = !currentPurchasable || isBusy || isDawnDisabled || submitButton.disabled;
      stickyCta.classList.toggle("loading", isBusy);
      stickySpinner?.classList.toggle("hidden", !isBusy);
      if (isBusy) {
        submitButton.setAttribute("aria-busy", "true");
        stickyCta.setAttribute("aria-busy", "true");
      } else {
        submitButton.removeAttribute("aria-busy");
        stickyCta.removeAttribute("aria-busy");
        if (stickyText && soldOutMessage && !soldOutMessage.classList.contains("hidden")) {
          stickyText.textContent = soldOutMessage.textContent.trim();
        }
      }
    };

    const synchronizePurchase = ({ updateUrl = false } = {}) => {
      if (twoStep) {
        submitButton.disabled = true;
        submitButton.setAttribute("aria-disabled", "true");
        if (stickyCta) stickyCta.disabled = true;
      }
      const variant = selectedVariant();
      if (!variant || !variantIdInput || !submitButton || !submitText) return;

      const allocations = Array.isArray(variant.sellingPlans) ? variant.sellingPlans : [];
      const allocationById = new Map(allocations.map((allocation) => [String(allocation.id), allocation]));
      const requiresSellingPlan = Boolean(variant.requiresSellingPlan);
      const subscriptionSelected = Boolean(allocations.length && (requiresSellingPlan || subscriptionPreference));
      const missingSubscription = twoStep && !allocations.length && (requiresSellingPlan || subscriptionPreference);

      if (subscriptionToggle) {
        subscriptionToggle.checked = subscriptionSelected;
        subscriptionToggle.disabled = !allocations.length || requiresSellingPlan;
        if (requiresSellingPlan && subscriptionRequired?.id) {
          subscriptionToggle.setAttribute("aria-describedby", subscriptionRequired.id);
        } else {
          subscriptionToggle.removeAttribute("aria-describedby");
        }
      }
      subscriptionCard?.classList.toggle("is-unavailable", !allocations.length);
      if (twoStep && oneTimeOption) {
        oneTimeOption.disabled = requiresSellingPlan;
        oneTimeOption.checked = !subscriptionSelected && !requiresSellingPlan && !missingSubscription;
        oneTimeCard?.classList.toggle("is-unavailable", requiresSellingPlan);
        variantOptions.forEach((option) => {
          option.disabled = !variants.find((candidate) => String(candidate.id) === option.value)?.available;
        });
      }
      if (subscriptionRequired) subscriptionRequired.hidden = !requiresSellingPlan;

      sellingPlanOptions.forEach((option) => {
        const allocation = allocationById.get(option.value);
        const label = option.closest("label");
        const price = label?.querySelector("[data-orela-plan-price]");
        option.disabled = !allocation;
        label?.classList.toggle("is-unavailable", !allocation);
        if (price) price.textContent = allocation?.priceFormatted || "";
      });

      let selectedAllocation = null;
      if (subscriptionSelected) {
        let selectedPlanOption = sellingPlanOptions.find(
          (option) => option.checked && allocationById.has(option.value),
        );
        if (!selectedPlanOption) {
          selectedPlanOption = sellingPlanOptions.find((option) => allocationById.has(option.value));
          if (selectedPlanOption) selectedPlanOption.checked = true;
        }
        selectedAllocation = selectedPlanOption ? allocationById.get(selectedPlanOption.value) : allocations[0];
      }

      if (sellingPlanInput) {
        sellingPlanInput.value = selectedAllocation?.id || "";
        sellingPlanInput.disabled = !selectedAllocation;
      }
      if (sellingPlansContainer) sellingPlansContainer.hidden = true;
      updateBundlePrices(subscriptionSelected, selectedAllocation?.id || null);
      updateTwoStepPrices(variant, selectedAllocation);

      currentPurchasable = Boolean(variant.available && !missingSubscription && (!requiresSellingPlan || selectedAllocation));
      const unavailableLabel = variant.available ? (labels.unavailable || "Unavailable") : (labels.soldOut || "Sold out");
      const displayedPrice = subscriptionSelected ? selectedAllocation : variant;
      const showPricePair =
        currentPurchasable &&
        showCtaSavings &&
        Number(variant.compareAtPrice) > Number(displayedPrice?.price);
      const buttonLabel = currentPurchasable
        ? addToCartLabel
        : unavailableLabel;

      clearProductFormError();
      variantIdInput.value = variant.id;
      variantIdInput.disabled = !currentPurchasable;
      submitButton.disabled = !currentPurchasable;
      if (currentPurchasable && !submitButton.classList.contains("loading")) {
        submitButton.removeAttribute("aria-disabled");
      } else {
        submitButton.setAttribute("aria-disabled", "true");
      }
      updateCtaContents(
        buttonLabel,
        showPricePair ? variant.compareAtPriceFormatted : "",
        showPricePair || (twoStep && currentPurchasable) ? displayedPrice.priceFormatted : "",
      );

      if (twoStep && purchaseState) {
        purchaseState.textContent = missingSubscription ? labels.subscriptionUnavailable : (variant.available ? "" : labels.soldOut);
        purchaseState.hidden = !purchaseState.textContent;
      }

      updateSelectedStyles();
      synchronizeStickySubmitState();

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        if (selectedAllocation) {
          url.searchParams.set("selling_plan", selectedAllocation.id);
        } else {
          url.searchParams.delete("selling_plan");
        }
        if (twoStep) {
          if (oneTimeOption?.checked) url.searchParams.set("purchase_type", "one-time");
          else url.searchParams.delete("purchase_type");
        }
        window.history.replaceState({ ...(window.history.state || {}), path: url.href }, "", url.href);
      }
    };

    variantOptions.forEach((option) => {
      option.addEventListener("change", () => synchronizePurchase({ updateUrl: true }), {
        signal: controller.signal,
      });
    });
    subscriptionToggle?.addEventListener(
      "change",
      () => {
        subscriptionPreference = subscriptionToggle.checked;
        synchronizePurchase({ updateUrl: twoStep });
      },
      { signal: controller.signal },
    );
    if (twoStep && oneTimeOption) {
      oneTimeOption.addEventListener("change", () => {
        subscriptionPreference = !oneTimeOption.checked;
        synchronizePurchase({ updateUrl: true });
      }, { signal: controller.signal });
      window.addEventListener("popstate", () => {
        readPurchaseUrl();
        synchronizePurchase();
      }, { signal: controller.signal });
    }
    sellingPlanOptions.forEach((option) => {
      option.addEventListener("change", () => synchronizePurchase({ updateUrl: true }), {
        signal: controller.signal,
      });
    });

    synchronizePurchase();
    if (twoStep) {
      purchaseOptions.querySelectorAll("[data-orela-purchase-fieldset]").forEach((fieldset) => { fieldset.disabled = false; });
      purchaseOptions.dataset.orelaPurchaseReady = "true";
    }

    if (submitButton && stickyCta) {
      const submitObserver = new MutationObserver(synchronizeStickySubmitState);
      submitObserver.observe(submitButton, {
        attributes: true,
        attributeFilter: ["aria-disabled", "class", "disabled"],
      });
      cleanups.push(() => submitObserver.disconnect());
    }

    const synchronizeStickyCta = () => {
      if (!stickyCta || !primaryCta) return;
      const isMobile = window.matchMedia("(max-width: 520px)").matches;
      const buttonBox = primaryCta.getBoundingClientRect();
      const primaryIsVisible = buttonBox.bottom > 0 && buttonBox.top < window.innerHeight;
      const stickyIsHidden = isMobile && primaryIsVisible;
      stickyCta.classList.toggle("is-hidden", stickyIsHidden);
      if (stickyIsHidden) {
        stickyCta.setAttribute("aria-hidden", "true");
        stickyCta.setAttribute("tabindex", "-1");
      } else {
        stickyCta.removeAttribute("aria-hidden");
        stickyCta.removeAttribute("tabindex");
      }
    };

    synchronizeStickyCta();
    window.addEventListener("scroll", synchronizeStickyCta, { passive: true, signal: controller.signal });
    window.addEventListener("resize", synchronizeStickyCta, { passive: true, signal: controller.signal });

    if ("IntersectionObserver" in window && primaryCta && stickyCta) {
      const ctaObserver = new IntersectionObserver(synchronizeStickyCta, { threshold: 0.05 });
      ctaObserver.observe(primaryCta);
      cleanups.push(() => ctaObserver.disconnect());
    }
  };

  const initializeMiniReviews = (root, controller) => {
    const carousel = root.querySelector("[data-orela-mini-reviews]");
    const slides = carousel ? [...carousel.querySelectorAll("[data-orela-review-slide]")] : [];
    const previousButton = carousel?.querySelector("[data-orela-review-prev]");
    const nextButton = carousel?.querySelector("[data-orela-review-next]");
    const rotateMs = Number(carousel?.dataset.orelaReviewRotateMs);
    if (!slides.length) return;

    let currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));
    const showSlide = (index) => {
      currentIndex = modulo(index, slides.length);
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === currentIndex;
        slide.classList.toggle("is-active", isActive);
        slide.hidden = !isActive;
        slide.setAttribute("aria-hidden", String(!isActive));
      });
    };

    previousButton?.addEventListener("click", () => showSlide(currentIndex - 1), { signal: controller.signal });
    nextButton?.addEventListener("click", () => showSlide(currentIndex + 1), { signal: controller.signal });
    showSlide(currentIndex);

    if (
      slides.length > 1 &&
      Number.isFinite(rotateMs) &&
      rotateMs > 0 &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      const intervalId = window.setInterval(() => {
        if (!document.hidden) showSlide(currentIndex + 1);
      }, rotateMs);
      controller.signal.addEventListener("abort", () => window.clearInterval(intervalId), { once: true });
    }
  };

  const initializeOrderFeed = (root, cleanups) => {
    const slides = [...root.querySelectorAll("[data-orela-join-slide]")];
    if (!slides.length) return;

    let currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));
    const showSlide = (index) => {
      currentIndex = modulo(index, slides.length);
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === currentIndex;
        slide.classList.toggle("is-active", isActive);
        slide.hidden = !isActive;
        slide.setAttribute("aria-hidden", String(!isActive));
      });
    };

    showSlide(currentIndex);
    if (slides.length > 1 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const intervalId = window.setInterval(() => showSlide(currentIndex + 1), 3600);
      cleanups.push(() => window.clearInterval(intervalId));
    }
  };

  const initializeImageMarquee = (root, controller, cleanups) => {
    const marquee = root.querySelector("[data-orela-image-marquee]");
    const track = marquee?.querySelector("[data-orela-marquee-track]");
    const primaryGroup = marquee?.querySelector("[data-orela-marquee-group]:not([aria-hidden='true'])");
    if (!marquee || !track || !primaryGroup) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const configuredDuration = Number(marquee.dataset.loopDuration);
    const loopDuration = Number.isFinite(configuredDuration) ? Math.max(1000, configuredDuration) : 15000;
    let frameId = null;
    let lastTime = null;
    let distance = 0;
    let offset = 0;
    let isVisible = true;
    let isPointerPaused = false;

    const render = () => {
      track.style.transform = reducedMotion.matches ? "none" : `translate3d(${offset}px, 0, 0)`;
    };

    const measure = () => {
      distance = primaryGroup.getBoundingClientRect().width;
      if (!distance) return;
      offset = -((-offset) % distance);
      render();
    };

    const animate = (timestamp) => {
      frameId = window.requestAnimationFrame(animate);
      const shouldPause = reducedMotion.matches || document.hidden || !isVisible || isPointerPaused || !distance;
      if (shouldPause) {
        lastTime = timestamp;
        return;
      }

      if (lastTime === null) lastTime = timestamp;
      const delta = Math.min(timestamp - lastTime, 64);
      lastTime = timestamp;
      offset -= (distance / loopDuration) * delta;
      if (-offset >= distance) offset += distance;
      render();
    };

    const resetClock = () => {
      lastTime = null;
      if (reducedMotion.matches) {
        offset = 0;
        render();
      }
    };

    marquee.addEventListener(
      "mouseenter",
      () => {
        isPointerPaused = true;
      },
      { signal: controller.signal },
    );
    marquee.addEventListener(
      "mouseleave",
      () => {
        isPointerPaused = false;
        resetClock();
      },
      { signal: controller.signal },
    );
    document.addEventListener("visibilitychange", resetClock, { signal: controller.signal });

    const handleMotionPreference = () => {
      resetClock();
      measure();
    };
    reducedMotion.addEventListener?.("change", handleMotionPreference);
    cleanups.push(() => reducedMotion.removeEventListener?.("change", handleMotionPreference));

    if ("IntersectionObserver" in window) {
      const visibilityObserver = new IntersectionObserver((entries) => {
        isVisible = entries.some((entry) => entry.isIntersecting);
        resetClock();
      });
      visibilityObserver.observe(marquee);
      cleanups.push(() => visibilityObserver.disconnect());
    }

    if ("ResizeObserver" in window) {
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(marquee);
      cleanups.push(() => resizeObserver.disconnect());
    } else {
      window.addEventListener("resize", measure, { passive: true, signal: controller.signal });
    }

    measure();
    frameId = window.requestAnimationFrame(animate);
    cleanups.push(() => window.cancelAnimationFrame(frameId));
  };

  const initializeSection = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.orelaInitialized === "true") return;
    root.dataset.orelaInitialized = "true";
    const controller = new AbortController();
    const cleanups = [() => controller.abort()];

    initializeGallery(root, controller, cleanups);
    initializePurchaseOptions(root, controller, cleanups);
    initializeMiniReviews(root, controller);
    initializeOrderFeed(root, cleanups);
    initializeImageMarquee(root, controller, cleanups);

    root.orelaCleanup = () => {
      cleanups.splice(0).forEach((cleanup) => cleanup());
      delete root.dataset.orelaInitialized;
    };
  };

  const initializeAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(sectionSelector)) initializeSection(scope);
    scope.querySelectorAll?.(sectionSelector).forEach(initializeSection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.matches?.(sectionSelector) ? event.target : event.target.querySelector?.(sectionSelector);
    root?.orelaCleanup?.();
  });

  document.addEventListener("shopify:block:select", (event) => {
    const block = event.target;
    const root = block?.closest?.(sectionSelector);
    if (!root) return;

    const mediaSlide = block.matches?.("[data-orela-media-slide]")
      ? block
      : block.querySelector?.("[data-orela-media-slide]");
    if (mediaSlide) {
      const slides = [...root.querySelectorAll("[data-orela-media-slide]")];
      const index = slides.indexOf(mediaSlide);
      root.querySelector(`[data-orela-media-thumb][data-media-index="${index}"]`)?.click();
    }

    const selectableSlide = block.matches?.("[data-orela-review-slide], [data-orela-join-slide]")
      ? block
      : block.querySelector?.("[data-orela-review-slide], [data-orela-join-slide]");
    if (selectableSlide) {
      const selector = selectableSlide.matches("[data-orela-review-slide]")
        ? "[data-orela-review-slide]"
        : "[data-orela-join-slide]";
      root.querySelectorAll(selector).forEach((slide) => {
        const isSelected = slide === selectableSlide;
        slide.classList.toggle("is-active", isSelected);
        slide.hidden = !isSelected;
        slide.setAttribute("aria-hidden", String(!isSelected));
      });
    }

    window.requestAnimationFrame(() => block.scrollIntoView?.({ behavior: "smooth", block: "center" }));
  });
})();
